import { QueryStatus } from '@reduxjs/toolkit/query';
import { addToast } from '@ydistri/utils';
import { useAppDispatch } from '../../../../store';
import { problemMessagesOf } from '../../../Configuration/Validator/problemsLib';
import {
  isEntityListImportValidationChanged,
  PutEntityListItemsRequest,
  useLazyGetEntityListQuery,
  usePutEntityListItemsMutation,
} from '../apiEntityLists';
import {
  resetImportForAllTypes,
  setImportStepRunning,
  setSelectedEntityList,
} from '../entityListAdministrationSlice';
import {
  EntityListTypeSupported,
  getEntityListSpecification,
  IMPORT_STEPS,
} from '../entityListsLib';
import { useCurrentEntityListState } from './useCurrentEntityListState';

export const useFinalizeEntityListImport = (entityListType: EntityListTypeSupported) => {
  const dispatch = useAppDispatch();
  const [putEntityListItems, { status }] = usePutEntityListItemsMutation();
  const [getEntityList] = useLazyGetEntityListQuery();
  const state = useCurrentEntityListState(entityListType);
  const pending =
    status === QueryStatus.pending || !!state?.importStepRunning[IMPORT_STEPS.ITEM_VALIDATION];
  const title = getEntityListSpecification(entityListType).title;

  const finalize = async (
    request: PutEntityListItemsRequest,
  ): Promise<'completed' | 'stale' | 'failed'> => {
    if (pending || !request.ignoreValidationToken) return 'failed';
    dispatch(
      setImportStepRunning({
        entityListType,
        data: { step: IMPORT_STEPS.ITEM_VALIDATION, value: true },
      }),
    );
    try {
      await putEntityListItems(request).unwrap();
      // Saving has succeeded even if the subsequent detail refresh fails.
      const updated = await getEntityList({ entityListType, entityListId: request.entityListId })
        .unwrap()
        .catch(() => undefined);
      if (updated) dispatch(setSelectedEntityList({ entityListType, data: updated }));
      dispatch(resetImportForAllTypes({ entityListId: request.entityListId }));
      dispatch(
        addToast({
          isError: false,
          message: `${title} was finalized. Any new or re-enabled SKUs will be ready when processing finishes.`,
        }),
      );
      return 'completed';
    } catch (error) {
      dispatch(
        addToast({
          isError: true,
          message: problemMessagesOf(
            error,
            `Finalization of ${title} was unsuccessful. Please try again.`,
          ),
        }),
      );
      return isEntityListImportValidationChanged(error) ? 'stale' : 'failed';
    } finally {
      dispatch(
        setImportStepRunning({
          entityListType,
          data: { step: IMPORT_STEPS.ITEM_VALIDATION, value: false },
        }),
      );
    }
  };

  return { finalize, pending };
};
